import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import BrandLogo from "../../utils/logoImg";
import { useBranding } from "../../context/BrandingContext";
import LoginVisualSvg from "./LoginVisualSvg";

/**
 * Split auth layout:
 * Left  = admin festival image OR theme SVG
 * Right = login card panel
 * Middle = soft bridge layer so both feel joined
 */
const AuthShell = ({
  children,
  eyebrow,
  title,
  subtitle,
  showBanner = true,
  bannerImages,
  footer,
  className = "",
}) => {
  const { settings, mediaUrl, companyName } = useBranding();
  const [imgFailed, setImgFailed] = useState(false);

  const bannerSrc = useMemo(() => {
    let url = null;
    if (Array.isArray(bannerImages) && bannerImages[0]) {
      url = bannerImages[0];
    } else if (
      settings?.loginBannerEnabled !== false &&
      settings?.loginBannerUrl
    ) {
      url = mediaUrl(settings.loginBannerUrl);
    }
    if (!url) return null;
    const stamp = settings?.updatedAt
      ? new Date(settings.updatedAt).getTime()
      : "";
    if (!stamp || Number.isNaN(stamp)) return url;
    return `${url}${url.includes("?") ? "&" : "?"}v=${stamp}`;
  }, [bannerImages, settings, mediaUrl]);

  useEffect(() => {
    setImgFailed(false);
  }, [bannerSrc]);

  const useImage =
    showBanner &&
    settings?.loginBannerEnabled !== false &&
    Boolean(bannerSrc) &&
    !imgFailed;

  return (
    <div className={`ds-auth ${className}`.trim()}>
      <div className="ds-auth__stage">
        {/* LEFT — image / SVG only (no text) */}
        <aside
          className="ds-auth__left"
          aria-label={`${companyName || "Brand"} login artwork`}
        >
          {useImage ? (
            <img
              src={bannerSrc}
              alt=""
              className="ds-auth__left-img"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <LoginVisualSvg className="ds-auth__left-svg" />
          )}
        </aside>

        {/* BRIDGE — joins left + right as one composition */}
        <div className="ds-auth__bridge" aria-hidden />

        {/* RIGHT — login card */}
        <section className="ds-auth__right">
          <motion.div
            className="ds-auth__card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="ds-auth__logo">
              <BrandLogo size="lg" />
            </div>

            {eyebrow ? <p className="ds-auth__eyebrow">{eyebrow}</p> : null}
            {title ? <h1 className="ds-auth__title">{title}</h1> : null}
            {subtitle ? <p className="ds-auth__subtitle">{subtitle}</p> : null}

            <div className="ds-auth__body">{children}</div>

            {footer ? <div className="ds-auth__footer">{footer}</div> : null}
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default AuthShell;
